import boxOfferPlanIcon from "@images/illustrations/boxOfferPlanIcon.svg"
import { BoxTerminatedIcon } from "@images/svgIcons/boxTerminatedIcon"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { MemberDetailedEntity } from "@modules/members/entities/memberEntity"
import { MemberDaytoDayFormikType, memberDaytoDayFormikValidationSchema } from "@modules/members/formik/memberFormik"
import { MemberStatus } from "@modules/members/gateway/memberDto"
import { useMember } from "@modules/members/hooks/useMember"
import { convertLasVisitedToNumberAndDays } from "@modules/members/memberUtils"
import {
  MemberSendOfferModal,
  MemberSendOfferModalRef,
} from "@modules/members/sendOfferToMember/components/memberSendOfferModal"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HuCamembert } from "@modules/ui/components/huCambert"
import { HuNextBookingCard } from "@modules/ui/components/huEventMiniCard"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { getIn, useFormik } from "formik"
import { Skeleton } from "primereact/skeleton"
import { useEffect, useRef, useState } from "react"
import { useTheme } from "styled-components"
import { FreeMode, Navigation, Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"

type MemberTabDayToDayProps = {
  memberDetailed: MemberDetailedEntity
} & React.HTMLAttributes<HTMLDivElement>

export const MemberTabDayToDay: React.FC<MemberTabDayToDayProps> = ({ memberDetailed, ...props }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [lastvisited, setLastVisited] = useState({
    number: 0,
    daysSinceLastVisited: 0,
  })

  const theme = useTheme()
  const toast = useToast()
  const { hideSidebar } = useSidebar()
  const canEditMember =
    useBoxStaffPermission(BoxStaffPermissions.EDIT_MEMBER) && memberDetailed.status !== MemberStatus.TERMINATED
  const canArchiveMember =
    useBoxStaffPermission(BoxStaffPermissions.ARCHIVE_MEMBER) && memberDetailed.status !== MemberStatus.TERMINATED
  const canSendOffer = useBoxStaffPermission(BoxStaffPermissions.SEND_OFFER)
  const confirmPopup = useConfirmPopup()
  const { archiveMember, updateMember } = useMember(memberDetailed.id)

  const memberSendOfferModalRef = useRef<MemberSendOfferModalRef>(null)

  const formik = useFormik<MemberDaytoDayFormikType>({
    enableReinitialize: true,
    initialValues: { note: memberDetailed.note || "" },
    validationSchema: memberDaytoDayFormikValidationSchema(memberDetailed.note || ""),
    onSubmit: async () => {
      setIsSubmitting(false)
      try {
        if (memberDetailed) {
          await updateMember(memberDetailed.setNote(formik.values.note))
        }
        toast.show({
          severity: "success",
          summary: word("member.editMember.success"),
        })
      } catch (e: any) {
        console.error(LoggerType.Service + "update member", e)
        toast.show({ severity: "error", summary: "Error", detail: e.message })
        setIsSubmitting(true)
      }
    },
  })

  useEffect(() => {
    if (memberDetailed.lastVisited) {
      const convert = convertLasVisitedToNumberAndDays(memberDetailed.lastVisited)
      setLastVisited({
        number: convert.number,
        daysSinceLastVisited: convert.daysSinceLastVisited,
      })
    }
  }, [memberDetailed])

  /* Used to check if the formik has changed value and trigger the save button */
  const value = getIn(formik.values, "note")
  const initialValue = getIn(formik.initialValues, "note")

  useEffect(() => {
    setIsSubmitting(value !== initialValue)
  }, [value])

  const handleOnSubmit = async () => {
    formik.submitForm()
  }

  const handleOnArchiveMember = async () => {
    confirmPopup.show({
      title: word("box.member.archive.confirmPopup.title"),
      message: word("box.member.archive.confirmPopup.description"),
      accept: async () => {
        try {
          await archiveMember()
          hideSidebar()
          toast.show({
            severity: "success",
            summary: word("box.member.archive.confirmPopup.success"),
          })
        } catch (error) {
          toast.show({
            severity: "error",
            summary: word("box.member.archive.confirmPopup.error"),
          })
        }
      },
      footerProps: {
        align: true,
        confirmClassName: "flex-1",
        cancelClassName: "flex-1",
        footerClassName: "flex flex-row justify-content-around w-full",
        confirmText: word("box.member.archive.confirmPopup.confirm"),
      },
    })
  }

  const handleSendOfferModal = () => {
    memberSendOfferModalRef.current?.open()
  }

  return (
    <div className="flex flex-column w-full gap-5" {...props}>
      {/* LAST VISIT & NO SHOW */}
      <div className="flex mt-2">
        {/* LAST VISIT*/}
        <div className="flex flex-1 flex-column">
          <HUText fontStyle="TM" color={theme.mainWhite}>
            {word("box.member.table.lastVisit")}
          </HUText>
          <div className="flex gap-2 align-items-center mt-2">
            <HuCamembert number={lastvisited.number} />
            <div className="flex flex-column">
              {memberDetailed.lastVisited ? (
                <>
                  <HUText fontStyle="BM" color={theme.mainWhite}>
                    {word("box.member.table.daysAgo", {
                      count: lastvisited.daysSinceLastVisited === 0 ? 1 : lastvisited.daysSinceLastVisited,
                    })}
                  </HUText>

                  <HUText fontStyle="BS" color={theme.neutralColor400}>
                    {`(${formatDateWithHyphen(dayjs(memberDetailed.lastVisited).format("L"))})`}
                  </HUText>
                </>
              ) : (
                <HUText fontStyle="BM" color={theme.neutralColor400}>
                  {word("box.member.table.noVisitYet")}
                </HUText>
              )}
            </div>
          </div>
        </div>
        {/* NO SHOW */}
        <div className="flex flex-1 flex-column ">
          <HUText fontStyle="TM" color={theme.mainWhite}>
            {word("box.member.table.noShow", {
              count: memberDetailed.noShowPastMonth === 0 ? 1 : memberDetailed.noShowPastMonth,
            })}
          </HUText>
          <div className="flex gap-2 align-items-center mt-2">
            <HuCamembert number={memberDetailed.convertNoShow()} />
            <div className="flex flex-column">
              <HUText fontStyle="BM" color={theme.mainWhite}>
                {memberDetailed.noShowPastMonth}{" "}
                {word("box.member.table.noShow", {
                  count: memberDetailed.noShowPastMonth === 0 ? 1 : memberDetailed.noShowPastMonth,
                }).toLowerCase()}
              </HUText>
              <HUText fontStyle="BS" color={theme.neutralColor400}>
                {word("box.member.table.dayToDay.pastMonth")}
              </HUText>
            </div>
          </div>
        </div>
      </div>
      {/* NEXT BOOKING */}
      <div className="flex flex-column ">
        <HUText fontStyle="TM" color={theme.mainWhite}>
          {word("box.member.table.dayToDay.nextBooking_plural", { count: memberDetailed.nextBookings.length })}
        </HUText>
        <div className="mt-2">
          {memberDetailed.nextBookings && memberDetailed.nextBookings.length > 0 ? (
            <Swiper
              spaceBetween={10}
              slidesPerView={3}
              freeMode={true}
              grabCursor={true}
              pagination={{
                clickable: true,
              }}
              modules={[FreeMode, Pagination, Navigation]}
            >
              {memberDetailed.nextBookings.map((booking) => (
                <SwiperSlide key={booking.slotId}>
                  <HuNextBookingCard nextBooking={booking} />
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <HuNextBookingCard />
          )}
        </div>
      </div>
      {/* ASSIGNED COACH */}
      {/* <div className="flex flex-column">
        <HUText fontStyle="TM" color={theme.mainWhite}>
          {word("box.member.table.assignedCoach.label")}
        </HUText>
        <HUText fontStyle="BS" color={theme.neutralColor400} className="mt-2">
          {word("box.member.table.assignedCoach.subtitle")}
        </HUText>
      </div> */}
      {/* INTERNAL NOTE*/}
      <div className="flex flex-column gap-2">
        <HUText fontStyle="TM" color={theme.mainWhite}>
          {word("box.member.table.dayToDay.internalNote.label")}
        </HUText>
        <div className="flex flex-row gap-2 mt-2 mb-2">
          <WarningIcon color={theme.neutralColor900} width={24} height={24} className="w-1" />
          <HUText fontStyle="BS" color={theme.neutralColor400}>
            {word("box.member.table.dayToDay.internalNote.warning")}
          </HUText>
        </div>
        <FormikInput
          flex
          formikStyle={{ flexGrow: 1 }}
          type="textarea"
          placeHolder={word("member.form.label.note")}
          getFieldProps={formik.getFieldProps}
          isInvalid={isFormFieldInValid("note", formik)}
          name="note"
          error={getFormErrorMessage("note", formik)}
          disabled={!canEditMember}
        />
      </div>
      {/* ACTIVE OFFERS */}
      <div className="flex flex-column gap-3">
        <HUText fontStyle="TM" color={theme.mainWhite}>
          {word("box.member.table.activeOffer.label", {
            count: memberDetailed.activeOffersTitles?.length === 0 ? 1 : 2,
          })}
        </HUText>
        <HUText fontStyle="BS" color={theme.neutralColor700}>
          {word("box.member.table.activeOffer.subtitle")}
        </HUText>

        {memberDetailed.activeOffersTitles && memberDetailed.activeOffersTitles.length > 0 ? (
          <div className="flex flex-wrap card mt-2 p-4 border-1 border-electricPurple-500">
            {memberDetailed.activeOffersTitles.map((offer, index) => (
              <HUText
                key={index}
                fontStyle="LM"
                color={theme.neutralColor100}
                className="m-1 bg-neutral-50 p-3 border-round-3xl"
              >
                {offer}
              </HUText>
            ))}
          </div>
        ) : (
          <>
            <HUText fontStyle="BM">{word("box.member.table.activeOffer.empty")}</HUText>
            <div className="flex flex-row gap-3 align-items-center justify-content-between">
              {/* Archive member */}
              {canArchiveMember && (
                <div
                  onClick={handleOnArchiveMember}
                  className="
                flex flex-column justify-content-center align-items-center
                card border-1 border-neutral-600
                py-2 px-4 gap-2 mb-0 text-center flex-1
                transition-duration-100 transition-all
                hover:surface-100 cursor-pointer
                "
                >
                  <BoxTerminatedIcon color={""} />
                  <HUText fontStyle="TS" color={theme.mainWhite}>
                    {word("box.member.table.dayToDay.archiveMember")}
                  </HUText>
                </div>
              )}
              {/* Send an offer */}
              {canSendOffer && (
                <div
                  onClick={handleSendOfferModal}
                  className="
              flex flex-column justify-content-center align-items-center
              card border-1 border-neutral-600
              py-2 px-4 gap-2 mb-0 text-center flex-1
              transition-duration-100 transition-all
              hover:surface-100 cursor-pointer
              "
                >
                  <img src={boxOfferPlanIcon} />
                  <HUText fontStyle="TS" color={theme.mainWhite}>
                    {word("box.member.table.dayToDay.sendAnOffer")}
                  </HUText>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {/* PERMISSIONS - Head-coach & Coach & Financial permissions denied */}
      {canEditMember && (
        // BUTTON
        <div className=" mb-4 flex flex-row gap-2 align-items-center justify-content-center">
          <HUButton
            type="Left_Icon"
            size="M"
            colorType="Quaternary"
            text={word("global.cancel")}
            onClick={() => hideSidebar()}
            className="w-full"
          />
          <HUButton
            type="Right_Icon"
            size="M"
            colorType="Primary"
            text={word("global.save")}
            onClick={() => isSubmitting && handleOnSubmit()}
            disabled={!isSubmitting}
            className="w-full"
          />
        </div>
      )}

      <MemberSendOfferModal userId={memberDetailed.userId} ref={memberSendOfferModalRef} />
    </div>
  )
}

type MemberTabDayToDaySkeletonProps = {} & React.HTMLAttributes<HTMLDivElement>
export const MemberTabDayToDaySkeleton: React.FC<MemberTabDayToDaySkeletonProps> = ({ ...props }) => {
  const theme = useTheme()

  return (
    <div className="flex flex-column w-full gap-5" {...props}>
      {/* LAST VISIT & NO SHOW */}
      <div className="flex mt-2">
        {/* LAST VISIT*/}
        <div className="flex flex-1 flex-column">
          <HUText fontStyle="TM" color={theme.mainWhite}>
            {word("box.member.table.lastVisit")}
          </HUText>
          <div className="flex gap-2 align-items-center mt-2">
            <Skeleton width="6rem" height="3rem" borderRadius="8px" />
            <div className="flex flex-column gap-1">
              <Skeleton width="7rem" borderRadius="8px" />
              <Skeleton width="6rem" borderRadius="8px" />
            </div>
          </div>
        </div>
        {/* NO SHOW */}
        <div className="flex flex-1 flex-column ">
          <HUText fontStyle="TM" color={theme.mainWhite}>
            {word("box.member.table.noShow", { count: 1 })}
          </HUText>
          <div className="flex gap-2 align-items-center mt-2">
            <Skeleton width="6rem" height="3rem" borderRadius="8px" />

            <div className="flex flex-column gap-1">
              <Skeleton width="7rem" borderRadius="8px" />
              <Skeleton width="8rem" borderRadius="8px" />
            </div>
          </div>
        </div>
      </div>
      {/* NEXT BOOKING */}
      <div className="flex flex-column ">
        <HUText fontStyle="TM" color={theme.mainWhite}>
          {word("box.member.table.dayToDay.nextBooking_plural", { count: 2 })}
        </HUText>
        <div className="flex flex-rows gap-2 mt-2">
          <Skeleton width="10rem" height="4.5rem" borderRadius="8px" />
          <Skeleton width="10rem" height="4.5rem" borderRadius="8px" />
          <Skeleton width="10rem" height="4.5rem" borderRadius="8px" />
        </div>
      </div>
      {/* INTERNAL NOTE*/}
      <div className="flex flex-column">
        <HUText fontStyle="TM" color={theme.mainWhite}>
          {word("box.member.table.dayToDay.internalNote.label")}
        </HUText>
        <div className="flex flex-row gap-2 mt-2 mb-2">
          <WarningIcon color={theme.neutralColor900} width={20} height={20} />
          <HUText fontStyle="BS" color={theme.neutralColor400}>
            {word("box.member.table.dayToDay.internalNote.warning")}
          </HUText>
        </div>
        <Skeleton height="7rem" borderRadius="8px" />
      </div>
      {/* ACTIVE OFFERS */}
      <div className="flex flex-column">
        <HUText fontStyle="TM" color={theme.mainWhite}>
          {word("box.member.table.activeOffer.label", { count: 1 })}
        </HUText>
        <HUText fontStyle="BS" color={theme.neutralColor400} className="mt-2">
          {word("box.member.table.activeOffer.subtitle")}
        </HUText>
        <Skeleton height="7rem" borderRadius="8px" className="mt-2" />
      </div>
      {/* BUTTON */}
      <div className="mb-4 flex flex-row gap-2 align-items-center justify-content-center">
        <Skeleton height="3.5rem" className="rounded-full" />
        <Skeleton height="3.5rem" className="rounded-full" />
      </div>
    </div>
  )
}
